import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { FaChevronRight, FaCalendarAlt, FaServer, FaNetworkWired, FaFileAlt } from 'react-icons/fa';
import LogFileRow from './LogFileRow';
import PropTypes from 'prop-types';

/**
 * LogPathRow Component
 * 
 * Displays a single log path with expandable content showing log files.
 * Optimized for performance with memoization and efficient re-rendering.
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.logPath - The log path data object
 * @param {boolean} props.expanded - Whether the row is expanded
 * @param {Function} props.onToggle - Function to call when toggling expansion
 * @param {boolean} props.showArchived - Whether to show archived logs
 * @param {number} props.lastUpdate - Timestamp of the last update to trigger re-renders
 */
const LogPathRow = memo(({ logPath, expanded, onToggle, showArchived, lastUpdate, filters }) => {
  // State for expanded files with localStorage persistence
  const [expandedFiles, setExpandedFiles] = useState(() => {
    try {
      const stored = localStorage.getItem(`logFile_expanded_${logPath.serverId}_${logPath.path}`);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.error('Error loading expanded files from localStorage:', e);
      return {};
    }
  });
  
  // Save expanded files to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem(
        `logFile_expanded_${logPath.serverId}_${logPath.path}`, 
        JSON.stringify(expandedFiles)
      );
    } catch (e) {
      console.error('Error saving expanded files to localStorage:', e);
    }
  }, [expandedFiles, logPath.serverId, logPath.path]);
  
  /**
   * Toggle expanded state for a log file
   * 
   * @param {string} file - The file path to toggle
   */
  const handleToggleFile = useCallback((file) => {
    setExpandedFiles(prev => ({
      ...prev,
      [file]: !prev[file]
    }));
  }, []);
  
  /**
   * Format timestamp for display
   * 
   * @param {number} timestamp - Timestamp in milliseconds
   * @returns {string} - Formatted time string
   */
  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    // For older dates, return the actual date
    return date.toLocaleString();
  }, []);
  
  // Calculate total event count across all files (removed unused variable)
  // const totalEvents = useMemo(() => {
  //   if (!logPath.files) return 0;
  //   return Object.values(logPath.files).reduce(
  //     (sum, file) => sum + file.events.length, 
  //     0
  //   );
  // }, [logPath.files]);
  
  // Memoize the sorted files to prevent unnecessary recalculations
  const sortedFiles = useMemo(() => {
    if (!logPath.files) return [];
    
    return Object.entries(logPath.files)
      .map(([file, fileData]) => ({
        file,
        ...fileData
      }))
      .sort((a, b) => b.lastUpdated - a.lastUpdated);
  }, [logPath.files]);

  return (
    <div className={`log-path-row ${expanded ? 'expanded' : ''} severity-${logPath.severity} health-${logPath.health}`}>
      <div className="log-path-header" onClick={onToggle}>
        <div className="log-path-toggle">
          <FaChevronRight className={expanded ? 'expanded' : ''} />
        </div>
        
        <div className="log-path-details">
          <div className="log-path-label">{logPath.path}</div>
          
          <div className="log-path-stats">
            <div className="log-path-stat">
              <FaFileAlt />
              <span>{logPath.today_files_count} files</span>
            </div>
            
            <div className="log-path-stat">
              <span className={`log-status-indicator ${logPath.status === 'active' ? 'log-status-active' : 'log-status-inactive'}`}></span>
              <span>{logPath.status}</span>
            </div>
            
            <div className="log-path-stat">
              <FaServer />
              <span>{logPath.serverId}</span>
            </div>
            
            <div className="log-path-stat">
              <FaNetworkWired />
              <span>{logPath.host_ip}</span>
            </div>
            
            <div className="log-path-stat">
              <FaCalendarAlt />
              <span>{formatTime(logPath.lastUpdated)}</span>
            </div>
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="log-files-container">
          {sortedFiles.length > 0 ? (
            sortedFiles.map(fileData => (
              <LogFileRow
                key={fileData.file}
                fileData={fileData}
                expanded={!!expandedFiles[fileData.file]}
                onToggle={() => handleToggleFile(fileData.file)}
                showArchived={showArchived}
                lastUpdate={lastUpdate}
                filters={filters}
              />
            ))
          ) : (
            <div className="log-files-empty">
              <p>No log files available for this path.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  // Also check filters for shallow equality
  return (
    prevProps.expanded === nextProps.expanded &&
    prevProps.showArchived === nextProps.showArchived &&
    prevProps.lastUpdate === nextProps.lastUpdate &&
    prevProps.logPath.lastUpdated === nextProps.logPath.lastUpdated &&
    JSON.stringify(prevProps.filters || {}) === JSON.stringify(nextProps.filters || {})
  );
});

LogPathRow.propTypes = {
  logPath: PropTypes.shape({
    serverId: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    status: PropTypes.string,
    severity: PropTypes.string,
    health: PropTypes.string,
    lastUpdated: PropTypes.number,
    today_files_count: PropTypes.number,
    host_name: PropTypes.string,
    host_ip: PropTypes.string,
    files: PropTypes.object
  }).isRequired,
  filters: PropTypes.object,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  showArchived: PropTypes.bool,
  lastUpdate: PropTypes.number
};

LogPathRow.defaultProps = {
  showArchived: false,
  lastUpdate: Date.now(),
  filters: {}
};

export default LogPathRow;
